import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Center,
  Code,
  CopyButton,
  Group,
  Loader,
  PasswordInput,
  SimpleGrid,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import { QRCodeSVG } from 'qrcode.react';
import { useState } from 'react';
import { z } from 'zod';

import { AppShell } from '../../components/app-shell/AppShell';
import { SettingsTabs } from '../../components/app-shell/SettingsTabs';
import { MotionSection, PageHero } from '../../components/ui';
import { useApiForm } from '../../hooks/useApiForm';
import { ApiError, fetchJson, mutateJson } from '../../lib/api';
import { track } from '../../lib/telemetry';
import { showToast } from '../../lib/toast';

/**
 * /settings/security — change password (live) + TOTP two-factor (live) + a
 * placeholder for per-device session management.
 *
 * Two-factor: enrol with an authenticator app, confirm a code to turn it on,
 * and receive one-time recovery codes. Admins are required to have it on
 * (enforced at the /admin gate). The crypto + sign-in challenge live server-side.
 */

const passwordSchema = z
  .object({
    current_password: z
      .string()
      .min(1, 'Enter your current password.')
      .max(200, 'Keep it under 200 characters.'),
    new_password: z
      .string()
      .min(10, 'At least 10 characters.')
      .max(128, 'Keep it under 128 characters.'),
    confirm_password: z.string(),
  })
  .refine((v) => v.new_password === v.confirm_password, {
    message: 'Passwords do not match.',
    path: ['confirm_password'],
  })
  .refine((v) => v.current_password !== v.new_password, {
    message: 'Pick a new password that’s different from the current one.',
    path: ['new_password'],
  });

type ChangePasswordValues = z.infer<typeof passwordSchema>;

type ChangePasswordResponse = {
  ok: boolean;
  message?: string;
  sessions_revoked?: number;
};

export const Route = createFileRoute('/settings/security')({
  component: SecuritySettingsPage,
});

function SecuritySettingsPage() {
  const { form, submit, submitting } = useApiForm<ChangePasswordValues, ChangePasswordResponse>({
    endpoint: '/api/auth/change-password',
    schema: passwordSchema,
    initialValues: {
      current_password: '',
      new_password: '',
      confirm_password: '',
    },
    transform: (values) => ({
      current_password: values.current_password,
      new_password: values.new_password,
    }),
    onSuccess: (data) => {
      track('auth.password_changed', { sessionsRevoked: data.sessions_revoked ?? 0 });
      const revoked = data.sessions_revoked ?? 0;
      const tail =
        revoked > 0
          ? ` Sessions on ${revoked} other ${revoked === 1 ? 'device' : 'devices'} have been signed out.`
          : '';
      showToast({ kind: 'success', body: `Password updated.${tail}` });
      form.reset();
    },
    mapError: (err) => {
      showToast({
        kind: 'error',
        body: err.message || 'Could not change your password.',
      });
      return null;
    },
  });

  return (
    <AppShell tone="form">
      <Stack gap="lg">
        <MotionSection>
          <PageHero
            eyebrow="Settings"
            title="Security"
            lede="Password, two-factor, and active sessions."
          />
        </MotionSection>

        <SettingsTabs current="security" />

        <Card withBorder padding="lg" radius="md">
          <form onSubmit={submit}>
            <Stack gap="md">
              <Text fw={600}>Change password</Text>
              <Text size="sm" c="dimmed">
                We’ll sign other devices out of your account. This device stays signed in.
              </Text>
              <PasswordInput
                label="Current password"
                {...form.getInputProps('current_password')}
                autoComplete="current-password"
              />
              <PasswordInput
                label="New password"
                description="At least 10 characters."
                {...form.getInputProps('new_password')}
                autoComplete="new-password"
              />
              <PasswordInput
                label="Confirm new password"
                {...form.getInputProps('confirm_password')}
                autoComplete="new-password"
              />
              <Button type="submit" loading={submitting}>
                Update password
              </Button>
            </Stack>
          </form>
        </Card>

        <TwoFactorCard />

        <Card withBorder padding="lg" radius="md">
          <Stack gap="xs">
            <Text fw={600}>Active sessions</Text>
            <Text size="sm" c="dimmed">
              Per-device session listing and remote-revoke land in the next pass. Updating your
              password signs out every other device immediately.
            </Text>
          </Stack>
        </Card>
      </Stack>
    </AppShell>
  );
}

// ── Two-factor (TOTP) ────────────────────────────────────────────────────────

function formatSecret(secret: string): string {
  return secret.replace(/(.{4})/g, '$1 ').trim();
}

function TwoFactorCard() {
  const qc = useQueryClient();
  const statusQ = useQuery({
    queryKey: ['mfa-status'],
    queryFn: () => fetchJson<{ enabled: boolean }>('/api/auth/mfa/status'),
  });

  const [enrollment, setEnrollment] = useState<{ secret: string; otpauthUri: string } | null>(null);
  const [code, setCode] = useState('');
  const [recoveryCodes, setRecoveryCodes] = useState<string[] | null>(null);
  const [turningOff, setTurningOff] = useState(false);
  const [disableCode, setDisableCode] = useState('');
  const [error, setError] = useState<string | null>(null);
  // AUD-025 — proof of possession before minting a pending secret. Kept as its
  // own field-level error so a bad password reads inline on the password
  // input rather than a generic alert.
  const [enrollPassword, setEnrollPassword] = useState('');
  const [enrollPasswordError, setEnrollPasswordError] = useState<string | null>(null);

  const enrollM = useMutation({
    mutationFn: () =>
      mutateJson<{ secret: string; otpauthUri: string }>('/api/auth/mfa/enroll', {
        current_password: enrollPassword,
      }),
    onSuccess: (d) => {
      setEnrollPasswordError(null);
      setEnrollPassword('');
      setEnrollment(d);
    },
    onError: (e: Error) => {
      // Only a 400 (the password proof-of-possession check) belongs on the
      // password field — a 429 rate limit or 409 already-enabled rendered
      // there reads as "your password is wrong" and sends the user off to
      // reset a password that was never the problem.
      if (e instanceof ApiError && e.status === 400) {
        setEnrollPasswordError(e.message);
      } else {
        setEnrollPasswordError(null);
        setError(e.message);
      }
    },
  });
  const verifyM = useMutation({
    mutationFn: () =>
      mutateJson<{ ok: boolean; recoveryCodes: string[] }>('/api/auth/mfa/verify', {
        code: code.trim(),
      }),
    onSuccess: (d) => {
      setError(null);
      setEnrollment(null);
      setCode('');
      setRecoveryCodes(d.recoveryCodes);
      void qc.invalidateQueries({ queryKey: ['mfa-status'] });
    },
    onError: (e: Error) => setError(e.message),
  });
  const disableM = useMutation({
    mutationFn: () =>
      mutateJson<{ ok: boolean }>('/api/auth/mfa/disable', { code: disableCode.trim() }),
    onSuccess: () => {
      setError(null);
      setTurningOff(false);
      setDisableCode('');
      showToast({ kind: 'success', body: 'Two-factor turned off.' });
      void qc.invalidateQueries({ queryKey: ['mfa-status'] });
    },
    onError: (e: Error) => setError(e.message),
  });

  const enabled = statusQ.data?.enabled === true;

  return (
    <Card withBorder padding="lg" radius="md">
      <Stack gap="md">
        <Group justify="space-between" align="center">
          <Text fw={600}>Two-factor authentication</Text>
          {statusQ.isLoading ? (
            <Loader size="xs" />
          ) : (
            <Badge color={enabled ? 'brand' : 'gray'} variant="light">
              {enabled ? 'On' : 'Off'}
            </Badge>
          )}
        </Group>

        {recoveryCodes !== null ? (
          <Stack gap="xs">
            <Alert color="brand" variant="light">
              Two-factor is on. Save these recovery codes somewhere safe. Each works once if you
              lose your authenticator. They won’t be shown again.
            </Alert>
            <SimpleGrid cols={2} spacing="xs">
              {recoveryCodes.map((rc) => (
                <Code key={rc} block>
                  {rc}
                </Code>
              ))}
            </SimpleGrid>
            <Button variant="light" w="fit-content" onClick={() => setRecoveryCodes(null)}>
              I’ve saved these
            </Button>
          </Stack>
        ) : enabled ? (
          turningOff ? (
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Enter a current authenticator code (or a recovery code) to turn two-factor off.
              </Text>
              <TextInput
                placeholder="123 456"
                inputMode="numeric"
                value={disableCode}
                onChange={(e) => setDisableCode(e.currentTarget.value)}
              />
              {error !== null && (
                <Alert color="red" variant="light">
                  {error}
                </Alert>
              )}
              <Group>
                <Button color="red" variant="light" loading={disableM.isPending} onClick={() => disableM.mutate()}>
                  Turn off
                </Button>
                <Button
                  variant="subtle"
                  onClick={() => {
                    setTurningOff(false);
                    setError(null);
                  }}
                >
                  Cancel
                </Button>
              </Group>
            </Stack>
          ) : (
            <Stack gap="xs">
              <Text size="sm" c="dimmed">
                Your account is protected by an authenticator app.
              </Text>
              <Button
                variant="light"
                color="red"
                w="fit-content"
                onClick={() => {
                  setTurningOff(true);
                  setError(null);
                }}
              >
                Turn off two-factor
              </Button>
            </Stack>
          )
        ) : enrollment !== null ? (
          <Stack gap="sm">
            <Text size="sm" c="dimmed">
              Scan this with your authenticator app (Google Authenticator, 1Password, Authy…), then
              enter the 6-digit code it shows.
            </Text>
            <Center>
              <Box
                bg="white"
                p="sm"
                style={{ borderRadius: 'var(--mantine-radius-md)', lineHeight: 0 }}
              >
                <QRCodeSVG
                  value={enrollment.otpauthUri}
                  size={192}
                  marginSize={2}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  title="WWAAVE two-factor setup QR code"
                />
              </Box>
            </Center>
            <Stack gap={4}>
              <Text size="xs" c="dimmed">
                Can’t scan it? Enter this key by hand instead:
              </Text>
              <Group gap="xs" wrap="nowrap">
                <Code block style={{ flex: 1, letterSpacing: '0.08em' }}>
                  {formatSecret(enrollment.secret)}
                </Code>
                <CopyButton value={enrollment.secret}>
                  {({ copied, copy }) => (
                    <Button variant="light" size="sm" onClick={copy}>
                      {copied ? 'Copied' : 'Copy'}
                    </Button>
                  )}
                </CopyButton>
              </Group>
            </Stack>
            <TextInput
              label="Enter the 6-digit code from your app"
              placeholder="123 456"
              inputMode="numeric"
              value={code}
              onChange={(e) => setCode(e.currentTarget.value)}
            />
            {error !== null && (
              <Alert color="red" variant="light">
                {error}
              </Alert>
            )}
            <Group>
              <Button loading={verifyM.isPending} onClick={() => verifyM.mutate()}>
                Verify and turn on
              </Button>
              <Button
                variant="subtle"
                onClick={() => {
                  setEnrollment(null);
                  setCode('');
                  setError(null);
                }}
              >
                Cancel
              </Button>
            </Group>
          </Stack>
        ) : (
          <Stack gap="xs">
            <Text size="sm" c="dimmed">
              Add a one-time code from an authenticator app to your sign-in. Required for admins.
            </Text>
            <PasswordInput
              label="Confirm your password to continue"
              placeholder="Current password"
              autoComplete="current-password"
              value={enrollPassword}
              onChange={(e) => {
                setEnrollPassword(e.currentTarget.value);
                if (enrollPasswordError !== null) setEnrollPasswordError(null);
              }}
              error={enrollPasswordError}
            />
            <Button
              variant="light"
              w="fit-content"
              loading={enrollM.isPending}
              disabled={enrollPassword.length === 0}
              onClick={() => enrollM.mutate()}
            >
              Set up two-factor
            </Button>
          </Stack>
        )}
      </Stack>
    </Card>
  );
}
